import type { Database } from 'sql.js';
import { queryOne } from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import { filterEntregasCatalogMax } from '../../../../shared/entrega/domain/entregaCatalogLimits.ts';
import type {
  EntregaCatalogRow,
  GrupoAlimentoRow,
  ProveedorRow,
  RedencionBonoCatalogSnapshot,
  TipoBonoCatalogRow,
} from '../../../domain/redencionBonoModel.ts';
import { sqlStringCol } from './mappers.ts';

/** Reads the voucher types catalog from the database. */
export function readTiposBonos(db: Database): TipoBonoCatalogRow[] {
  if (queryOne(db, `SELECT 1 FROM sqlite_master WHERE type='table' AND name='tipos_bonos'`, []) == null) {
    return [];
  }
  const st = db.prepare(`SELECT id, nombre FROM tipos_bonos WHERE estado = 1 ORDER BY id`);
  const out: TipoBonoCatalogRow[] = [];
  try {
    while (st.step()) {
      const o = st.getAsObject();
      out.push({
        id: Number(o.id),
        nombre: sqlStringCol(o.nombre),
      });
    }
  } finally {
    st.free();
  }
  return out;
}

/** Reads the delivery numbers catalog from the database. */
export function readEntregas(db: Database): EntregaCatalogRow[] {
  const st = db.prepare(`SELECT id, nombre FROM entregas WHERE estado = 1 ORDER BY nombre COLLATE NOCASE`);
  const out: EntregaCatalogRow[] = [];
  try {
    while (st.step()) {
      const o = st.getAsObject();
      out.push({
        id: Number(o.id),
        nombre: sqlStringCol(o.nombre),
      });
    }
  } finally {
    st.free();
  }
  return filterEntregasCatalogMax(out);
}

/** Reads the food groups catalog from the database. */
export function readGrupos(db: Database): GrupoAlimentoRow[] {
  const st = db.prepare(
    `SELECT id, public_id, nombre, estado, fecha_creacion, fecha_actualizacion FROM grupos_alimentos ORDER BY id`,
  );
  const out: GrupoAlimentoRow[] = [];
  try {
    while (st.step()) {
      const o = st.getAsObject();
      out.push({
        id: Number(o.id),
        publicId: sqlStringCol(o.public_id),
        nombre: sqlStringCol(o.nombre),
        estado: Number(o.estado ?? 0),
        fechaCreacion: sqlStringCol(o.fecha_creacion),
        fechaActualizacion: sqlStringCol(o.fecha_actualizacion),
      });
    }
  } finally {
    st.free();
  }
  return out;
}

/** Reads the providers catalog from the database. */
export function readProveedores(db: Database): ProveedorRow[] {
  const st = db.prepare(
    `SELECT p.id, p.public_id, p.nombre, p.direccion, p.latitud, p.longitud, p.comuna_id, p.estado, p.fecha_creacion, p.fecha_actualizacion,
            GROUP_CONCAT(pga.grupo_alimentos_id) as grupos_alimentos_ids
     FROM proveedores p
     LEFT JOIN proveedor_grupo_alimentos pga ON p.id = pga.proveedor_id
     GROUP BY p.id
     ORDER BY p.id`,
  );
  const out: ProveedorRow[] = [];
  try {
    while (st.step()) {
      const o = st.getAsObject();
      const gaIdsRaw = sqlStringCol(o.grupos_alimentos_ids);
      const gruposAlimentosIds = gaIdsRaw
        ? gaIdsRaw.split(',').map(Number).filter((n) => Number.isFinite(n) && n > 0)
        : [];
      out.push({
        id: Number(o.id),
        publicId: sqlStringCol(o.public_id),
        nombre: sqlStringCol(o.nombre),
        direccion: sqlStringCol(o.direccion),
        latitud: typeof o.latitud === 'string' || typeof o.latitud === 'number' ? String(o.latitud) : '',
        longitud: typeof o.longitud === 'string' || typeof o.longitud === 'number' ? String(o.longitud) : '',
        comunaId: Number(o.comuna_id ?? o.COMUNA_ID ?? 0),
        gruposAlimentosIds,
        estado: Number(o.estado ?? 0),
        fechaCreacion: sqlStringCol(o.fecha_creacion),
        fechaActualizacion: sqlStringCol(o.fecha_actualizacion),
      });
    }
  } finally {
    st.free();
  }
  return out;
}

/** Fetches the full redemption catalog snapshot. */
export function fetchRedencionBonoCatalog(db: Database): RedencionBonoCatalogSnapshot {
  return {
    entregas: readEntregas(db),
    gruposAlimentos: readGrupos(db),
    proveedores: readProveedores(db),
    tiposBonos: readTiposBonos(db),
  };
}
